import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ShieldAlert, Copy, UserX, Trash2, Loader2, Camera } from "lucide-react";
import adminApi, { apiError } from "@/lib/adminApi";
import { formatDateRange, formatPrice } from "@/lib/format";

const CopyButton = ({ value, testId }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={() => {
        navigator.clipboard?.writeText(value);
        setCopied(true);
        toast.success("Copié");
        setTimeout(() => setCopied(false), 1500);
      }}
      className="ml-2 text-[10px] font-bold uppercase tracking-wider text-white/35 hover:text-gold border border-white/10 hover:border-gold/40 px-2 py-0.5 transition-colors"
    >
      {copied ? "Copié" : <span className="inline-flex items-center gap-1"><Copy size={10} /> Copier</span>}
    </button>
  );
};

const Row = ({ label, value, copyTestId }) => (
  <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-1 py-2.5 border-b border-white/5">
    <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">{label}</span>
    <span className="text-white text-sm font-semibold sm:text-right">
      {value || "—"}
      {copyTestId && value ? <CopyButton value={value} testId={copyTestId} /> : null}
    </span>
  </div>
);

const Block = ({ title, children, danger = false }) => (
  <section className={`bg-noir-surface border p-6 ${danger ? "border-rouge/40" : "border-noir-border"}`}>
    <h2 className="font-display uppercase text-xl tracking-wide text-gold mb-4">{title}</h2>
    {children}
  </section>
);

const AdminRegistrationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [confirmAction, setConfirmAction] = useState(null); // 'cancel' | 'delete' | 'transfer'
  const [acting, setActing] = useState(false);
  const [stages, setStages] = useState([]);
  const [transferTarget, setTransferTarget] = useState("");

  const load = () =>
    adminApi.get(`/registrations/${id}`)
      .then(({ data }) => setData(data))
      .catch((e) => setError(apiError(e, "Inscription introuvable.")));

  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    adminApi.get("/stages").then(({ data }) => setStages(data)).catch(() => {});
  }, []);

  const runAction = async () => {
    setActing(true);
    try {
      if (confirmAction === "cancel") {
        const { data: res } = await adminApi.post(`/registrations/${id}/cancel`);
        toast.success(res.message);
        setConfirmAction(null);
        load();
      } else if (confirmAction === "transfer") {
        const { data: res } = await adminApi.post(`/registrations/${id}/transfer`, { target_stage_id: transferTarget });
        toast.success(res.message);
        setConfirmAction(null);
        setTransferTarget("");
        load();
      } else {
        await adminApi.delete(`/registrations/${id}`);
        toast.success("Inscription supprimée définitivement.");
        navigate("/admin/inscriptions");
      }
    } catch (e) {
      toast.error(apiError(e));
      setConfirmAction(null);
    } finally {
      setActing(false);
    }
  };

  if (error) {
    return (
      <div className="text-center py-20">
        <p className="text-rouge-hover">{error}</p>
        <Link to="/admin/inscriptions" className="mt-4 inline-block text-gold text-sm">← Retour aux inscriptions</Link>
      </div>
    );
  }
  if (!data) return <p className="text-white/40">Chargement…</p>;

  const { registration: r, stage, payment } = data;

  return (
    <div data-testid="admin-registration-detail" className="max-w-3xl space-y-6">
      <Link to="/admin/inscriptions" className="inline-flex items-center gap-2 text-xs text-white/40 hover:text-gold uppercase tracking-[0.2em] transition-colors">
        <ArrowLeft size={13} /> Retour aux inscriptions
      </Link>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-display uppercase text-4xl tracking-wide">
          {r.child_first_name} {r.child_last_name}
        </h1>
        <span data-testid="reg-reference" className="text-sm font-bold tracking-[0.15em] text-gold border border-gold/40 px-4 py-2">
          {r.registration_reference}
        </span>
      </div>

      <div
        data-testid="reg-image-rights-banner"
        className={`flex items-center gap-3 px-5 py-4 border text-sm font-bold uppercase tracking-[0.12em] ${
          r.image_rights_accepted ? "border-gold/40 bg-gold/5 text-gold" : "border-rouge/50 bg-rouge/10 text-rouge-hover"
        }`}
      >
        <Camera size={18} className="shrink-0" />
        {r.image_rights_accepted
          ? `Droit à l'image : accepté${r.image_rights_accepted_at ? ` le ${new Date(r.image_rights_accepted_at).toLocaleDateString("fr-FR")}` : ""}`
          : "Droit à l'image : NON accepté — ne pas publier de photos de cet enfant"}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Block title="Enfant">
          <Row label="Nom" value={r.child_last_name} />
          <Row label="Prénom" value={r.child_first_name} />
          <Row label="Date de naissance" value={r.child_birth_date} />
          <Row label="Catégorie" value={r.category} />
          <Row label="Club" value={r.current_club} />
          <Row label="Poste" value={r.playing_position} />
          <Row label="Gardien / Joueur" value={r.player_type === "gardien" ? "Gardien" : "Joueur"} />
        </Block>

        <Block title="Tenue officielle">
          <Row label="Taille maillot" value={r.jersey_size} />
          <Row label="Taille short" value={r.short_size} />
          <Row label="Taille chaussettes" value={r.sock_size} />
        </Block>

        <Block title="Responsable">
          <Row label="Email" value={r.email} copyTestId="copy-email" />
          <Row label="Téléphone mère" value={r.mother_phone} copyTestId="copy-mother-phone" />
          <Row label="Téléphone père" value={r.father_phone} copyTestId="copy-father-phone" />
        </Block>

        <Block title="Médical" danger>
          <p data-testid="medical-warning" className="flex items-start gap-2 text-xs text-rouge-hover font-bold uppercase tracking-[0.15em] mb-4">
            <ShieldAlert size={15} className="shrink-0" /> Données médicales — informations confidentielles
          </p>
          <Row label="Médecin traitant" value={r.doctor_name} />
          <Row label="Allergies alimentaires" value={r.food_allergies} />
          <Row label="Antécédents médicaux" value={r.medical_history} />
        </Block>

        <Block title="Inscription">
          <Row label="Stage" value={stage?.name} />
          {r.promo_code && <Row label="Code promo utilisé" value={`${r.promo_code} (−${r.discount_percent} %)`} />}
          <Row label="Date d'inscription" value={r.created_at ? new Date(r.created_at).toLocaleString("fr-FR") : "—"} />
          <Row label="Statut inscription" value={r.registration_status === "confirmed" ? "Confirmée" : r.registration_status === "pending_payment" ? "En attente de paiement" : r.registration_status === "cancelled" ? "Annulée" : r.registration_status} />
          <Row label="Statut paiement" value={r.payment_status === "paid" ? "PAYÉ" : r.payment_status === "failed" ? "ÉCHOUÉ" : "NON PAYÉ"} />
          {payment && (
            <>
              <Row label="Montant payé" value={formatPrice(payment.amount)} />
              <Row label="Date du paiement" value={payment.paid_at ? new Date(payment.paid_at).toLocaleString("fr-FR") : "—"} />
              <Row label="Référence Stripe" value={payment.stripe_payment_intent_id || payment.stripe_checkout_session_id} />
            </>
          )}
          <Row label="Droit à l'image" value={`${r.image_rights_accepted ? "Accepté" : "Non accepté"}${r.image_rights_accepted_at ? ` — le ${new Date(r.image_rights_accepted_at).toLocaleDateString("fr-FR")}` : ""}`} />
        </Block>
      </div>

      {stage && (
        <p className="text-white/35 text-xs">
          Stage : {stage.name} — les statuts de paiement sont déterminés exclusivement par Stripe et ne peuvent pas être modifiés manuellement.
        </p>
      )}

      {/* TRANSFERT DE STAGE */}
      {r.registration_status !== "cancelled" && (
        <section className="bg-noir-surface border border-gold/25 p-6">
          <h2 className="font-display uppercase text-xl tracking-wide text-gold mb-2">Transférer de stage</h2>
          <p className="text-white/50 text-sm leading-relaxed">
            Si la famille s'est trompée de stage : transférez l'enfant vers le bon stage. La place est libérée dans
            l'ancien stage et occupée dans le nouveau. Le paiement reste rattaché à l'inscription — aucun
            remboursement ni refacturation automatique.
          </p>
          <div className="mt-5 flex flex-col sm:flex-row gap-3">
            <select
              data-testid="transfer-stage-select"
              value={transferTarget}
              onChange={(e) => setTransferTarget(e.target.value)}
              className="flex-1 bg-[#050505] border border-white/10 text-white px-4 py-3 text-sm focus:border-gold outline-none"
            >
              <option value="">Choisir le nouveau stage…</option>
              {stages
                .filter((s) => s.id !== (stage && stage.id) && s.status !== "archived")
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} — {formatDateRange(s.start_date, s.end_date)} — {formatPrice(s.price)} — {s.places_restantes} place(s)
                  </option>
                ))}
            </select>
            <button
              data-testid="transfer-button"
              disabled={!transferTarget}
              onClick={() => setConfirmAction("transfer")}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gold text-noir text-xs font-bold uppercase tracking-[0.15em] rounded-full hover:bg-gold-light disabled:opacity-40 transition-colors whitespace-nowrap"
            >
              Transférer
            </button>
          </div>
        </section>
      )}

      {/* ZONE DÉSINSCRIPTION */}
      <section className="bg-noir-surface border border-rouge/30 p-6">
        <h2 className="font-display uppercase text-xl tracking-wide text-rouge-hover mb-2">Désinscription</h2>
        <p className="text-white/50 text-sm leading-relaxed">
          Si la famille souhaite se désinscrire : annulez l'inscription pour libérer immédiatement la place
          {r.payment_status === "paid" && " (le remboursement se fait ensuite depuis votre Dashboard Stripe)"}.
          {r.registration_status === "cancelled" || r.payment_status !== "paid"
            ? " Vous pouvez ensuite supprimer définitivement la fiche."
            : ""}
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          {r.registration_status !== "cancelled" && (
            <button
              data-testid="reg-cancel-button"
              onClick={() => setConfirmAction("cancel")}
              className="inline-flex items-center gap-2 px-6 py-3 border border-rouge/50 text-rouge-hover text-xs font-bold uppercase tracking-[0.15em] rounded-full hover:bg-rouge hover:text-white transition-colors"
            >
              <UserX size={14} /> Désinscrire ce participant
            </button>
          )}
          {(r.registration_status === "cancelled" || r.payment_status !== "paid") && (
            <button
              data-testid="reg-delete-button"
              onClick={() => setConfirmAction("delete")}
              className="inline-flex items-center gap-2 px-6 py-3 bg-rouge text-white text-xs font-bold uppercase tracking-[0.15em] rounded-full hover:bg-rouge-hover transition-colors"
            >
              <Trash2 size={14} /> Supprimer définitivement
            </button>
          )}
        </div>
      </section>

      {confirmAction && (
        <div data-testid="reg-action-confirm" className="fixed inset-0 z-50 bg-noir/90 backdrop-blur flex items-center justify-center p-6">
          <div className="bg-noir-surface border border-rouge/40 p-8 max-w-md w-full">
            <h2 className="font-display uppercase text-2xl tracking-wide text-rouge-hover">
              {confirmAction === "cancel" ? "Désinscrire ce participant ?" : confirmAction === "transfer" ? "Confirmer le transfert ?" : "Supprimer définitivement ?"}
            </h2>
            <p className="mt-3 text-white/60 text-sm">
              {confirmAction === "cancel" ? (
                <>
                  <strong className="text-white">{r.child_first_name} {r.child_last_name}</strong> sera désinscrit(e)
                  du stage et sa place sera immédiatement libérée.
                  {r.payment_status === "paid" && " Le paiement reste enregistré : pensez au remboursement dans Stripe si nécessaire."}
                </>
              ) : confirmAction === "transfer" ? (
                <>
                  <strong className="text-white">{r.child_first_name} {r.child_last_name}</strong> sera transféré(e) vers{" "}
                  <strong className="text-gold">{stages.find((s) => s.id === transferTarget)?.name}</strong>.
                  Sa place sera libérée dans « {stage?.name} » et occupée dans le nouveau stage.
                  Le paiement reste rattaché à l'inscription.
                </>
              ) : (
                <>La fiche de <strong className="text-white">{r.child_first_name} {r.child_last_name}</strong> sera supprimée sans retour possible.</>
              )}
            </p>
            <div className="mt-6 flex gap-3">
              <button
                data-testid="reg-action-yes"
                onClick={runAction}
                disabled={acting}
                className="px-6 py-3 bg-rouge text-white text-xs font-bold uppercase tracking-[0.15em] rounded-full hover:bg-rouge-hover disabled:opacity-60 flex items-center gap-2"
              >
                {acting && <Loader2 size={14} className="animate-spin" />} Confirmer
              </button>
              <button
                data-testid="reg-action-no"
                onClick={() => setConfirmAction(null)}
                className="px-6 py-3 border border-white/20 text-white text-xs font-bold uppercase tracking-[0.15em] rounded-full hover:border-white transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRegistrationDetail;
